import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useLocalInterviews from '@UI/Pages/Beneficiaries/Hooks/useLocalInterviews';
import useCapturePort from '@UI/Providers/PortsProvider/Hooks/Capture/useCapturePort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import { css } from '@emotion/react';
import { Button } from '@verclam/ui-kit';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

const messageCardStyle = css`
	width: 100%;
	max-width: 480px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.75rem;
	text-align: center;
	padding: 3rem ${theme.layout.gutter};
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
`;

const messageTitleStyle = css`
	${theme.typography.stateTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const messageDescriptionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	subject: SubjectAddress;
	localInterviewId: string;
};

const RecordingCrashFallback: React.FC<Props> = ({ subject, localInterviewId }) => {
	const navigate = useNavigate();
	const capturePort = useCapturePort();
	const outboxPort = useOutboxPort();
	const localInterviews = useLocalInterviews();
	const [isSettled, setIsSettled] = React.useState(false);
	const hasStartedRef = React.useRef(false);

	const outboxItemId = React.useMemo(
		() => localInterviews.find((candidate) => candidate.id === localInterviewId)?.outboxItemId ?? null,
		[localInterviews, localInterviewId],
	);

	React.useEffect(() => {
		if (hasStartedRef.current) {
			return;
		}
		hasStartedRef.current = true;

		void (async () => {
			try {
				const durationMs = capturePort.snapshot().recordedMs;
				await capturePort.stop();
				if (outboxItemId !== null) {
					await outboxPort.markReady(outboxItemId, { durationMs });
				}
			} catch {
				// A non-draft item means the transfer already started; a draft stays interrupted and is handled by that flow.
			}
			setIsSettled(true);
		})();
	}, [capturePort, outboxPort, outboxItemId]);

	const handleViewUpload = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/local-interviews/${localInterviewId}/upload`);
	}, [navigate, subject, localInterviewId]);

	const handleBackToDossier = React.useCallback(() => {
		navigate(buildSubjectPath(subject));
	}, [navigate, subject]);

	return (
		<div css={messageCardStyle} role="alert">
			<h2 css={messageTitleStyle}>Une erreur inattendue a interrompu l'écran d'enregistrement.</h2>
			<ConditionalLoader
				data={outboxItemId}
				fallback={
					<React.Fragment>
						<p css={messageDescriptionStyle}>
							L'enregistrement n'avait pas commencé : aucun son n'a été perdu.
						</p>
						<Button label="Retour au dossier" onClick={handleBackToDossier} />
					</React.Fragment>
				}
			>
				{() => (
					<React.Fragment>
						<p css={messageDescriptionStyle}>
							L'enregistrement a été arrêté. Ce qui a été capté est conservé sur cet appareil et
							sera envoyé.
						</p>
						<Button label="Voir l'envoi" disabled={!isSettled} onClick={handleViewUpload} />
					</React.Fragment>
				)}
			</ConditionalLoader>
		</div>
	);
};

export default RecordingCrashFallback;
